import { useState, useEffect, useCallback } from "react";
import { User } from "../types";
import { AuthView } from "../types/navigation";
import { apiService } from "../utils/apiService";
import { clearCachedUsers } from "../utils/dataCache";
import { navigateToView } from "../utils/appRoutes";
import { mapUserDataFromBackend } from "../utils/userDataMapper";

const RESET_TOKEN_STORAGE_KEY = "rio_reset_token";

const getResetTokenFromLocation = (): string | null => {
  if (typeof window === "undefined") return null;
  const fromSearch = new URLSearchParams(window.location.search).get("token");
  if (fromSearch) return fromSearch;
  const hash = window.location.hash.slice(1);
  if (!hash) return null;
  const queryIndex = hash.indexOf("?");
  const params = new URLSearchParams(
    queryIndex === -1 ? hash : hash.slice(queryIndex + 1),
  );
  return params.get("token");
};

const getAuthViewFromPath = (pathname: string): AuthView => {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  if (normalized.endsWith("/reset-password")) return "reset-password";
  if (normalized.endsWith("/forgot-password")) return "forgot-password";
  return "main";
};

export function useAuthSession(onLoginSuccess?: () => void) {
  const [currentUser, setCurrentUser] = useState<User | null>(() => {
    if (typeof window === "undefined") return null;
    try {
      const raw = localStorage.getItem("currentUser");
      if (!raw) return null;
      return JSON.parse(raw) as User;
    } catch {
      return null;
    }
  });

  const [authView, setAuthView] = useState<AuthView>(() => {
    if (typeof window === "undefined") return "main";
    return getAuthViewFromPath(window.location.pathname);
  });

  const [resetToken, setResetToken] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    const fromLocation = getResetTokenFromLocation();
    if (fromLocation) return fromLocation;
    try {
      return sessionStorage.getItem(RESET_TOKEN_STORAGE_KEY);
    } catch {
      return null;
    }
  });

  // Local storage is only a UI cache. The HttpOnly server session remains the
  // source of truth and must be confirmed before retaining a cached login.
  useEffect(() => {
    if (!currentUser?.id) return;
    let active = true;

    const validateServerSession = async () => {
      try {
        const response = await apiService.getMe();
        const rawUser = response.data?.user;
        if (!active) return;
        if (!rawUser) throw new Error("Session expired");

        const verifiedUser = mapUserDataFromBackend(rawUser);
        setCurrentUser(verifiedUser);
        localStorage.setItem("currentUser", JSON.stringify(verifiedUser));
      } catch {
        if (!active) return;
        clearCachedUsers();
        setCurrentUser(null);
        localStorage.removeItem("currentUser");
        localStorage.removeItem("authToken");
        const url = new URL(window.location.href);
        url.pathname = "/login";
        url.search = "";
        url.hash = "";
        window.history.replaceState({}, "", url);
      }
    };

    validateServerSession();
    return () => {
      active = false;
    };
  }, [currentUser?.id]);

  // Sync auth routes
  useEffect(() => {
    const applyAuthRoute = () => {
      if (currentUser) return;
      const view = getAuthViewFromPath(window.location.pathname);
      if (view === "reset-password") {
        const token = getResetTokenFromLocation();
        if (token) {
          try {
            sessionStorage.setItem(RESET_TOKEN_STORAGE_KEY, token);
          } catch {}
          const url = new URL(window.location.href);
          if (url.searchParams.has("token")) {
            url.searchParams.delete("token");
          }
          url.hash = "";
          window.history.replaceState({}, "", url.toString());
        }
        const storedToken = token
          ? token
          : (() => {
              try {
                return sessionStorage.getItem(RESET_TOKEN_STORAGE_KEY);
              } catch {
                return null;
              }
            })();
        setResetToken(storedToken);
      } else {
        setResetToken(null);
      }
      setAuthView(view);
    };

    applyAuthRoute();
    window.addEventListener("popstate", applyAuthRoute);
    return () => window.removeEventListener("popstate", applyAuthRoute);
  }, [currentUser]);

  // Handle session expiration across tabs and failed API queries
  useEffect(() => {
    const handleExpired = () => {
      setCurrentUser(null);
      setAuthView("main");
      try {
        localStorage.removeItem("currentUser");
        localStorage.removeItem("authToken");
      } catch {}
      try {
        const url = new URL(window.location.href);
        url.pathname = "/login";
        url.hash = "";
        url.search = "";
        window.history.replaceState({}, "", url.toString());
      } catch {}
    };

    window.addEventListener("rio:session-expired", handleExpired);
    return () => window.removeEventListener("rio:session-expired", handleExpired);
  }, []);

  // Sync pathname on auth view change
  useEffect(() => {
    if (currentUser) return;
    let hasStoredUser = false;
    try {
      hasStoredUser = Boolean(localStorage.getItem("currentUser"));
    } catch {}
    if (hasStoredUser) return;
    const path =
      authView === "reset-password"
        ? "/reset-password"
        : authView === "forgot-password"
        ? "/forgot-password"
        : "/login";
    try {
      const url = new URL(window.location.href);
      if (url.pathname !== path || url.hash) {
        url.pathname = path;
        url.hash = "";
        url.search = "";
        window.history.replaceState({}, "", url.toString());
      }
    } catch {}
  }, [authView, currentUser]);

  const handleLogin = useCallback(
    (user: User) => {
      clearCachedUsers();
      setCurrentUser(user);
      try {
        localStorage.setItem("currentUser", JSON.stringify(user));
      } catch {}
      navigateToView("dashboard", true);
      if (onLoginSuccess) {
        onLoginSuccess();
      }
    },
    [onLoginSuccess]
  );

  const handleUpdateCurrentUser = useCallback((updatedUser: User) => {
    clearCachedUsers();
    setCurrentUser(updatedUser);
    try {
      localStorage.setItem("currentUser", JSON.stringify(updatedUser));
    } catch {}
  }, []);

  const handleLogout = useCallback(() => {
    clearCachedUsers();
    apiService.logout();
    setCurrentUser(null);
    setAuthView("main");
    try {
      localStorage.removeItem("currentUser");
    } catch {}
    try {
      const url = new URL(window.location.href);
      url.pathname = "/login";
      url.search = "";
      url.hash = "";
      window.history.replaceState({}, "", url.toString());
    } catch {}
  }, []);

  const handleShowForgotPassword = useCallback(() => {
    setAuthView("forgot-password");
    try {
      const url = new URL(window.location.href);
      url.pathname = "/forgot-password";
      url.hash = "";
      url.search = "";
      window.history.replaceState({}, "", url.toString());
    } catch {}
  }, []);

  const handleBackToMain = useCallback(() => {
    setAuthView("main");
    setResetToken(null);
    try {
      sessionStorage.removeItem(RESET_TOKEN_STORAGE_KEY);
    } catch {}
    try {
      const url = new URL(window.location.href);
      url.pathname = "/login";
      url.search = "";
      url.hash = "";
      window.history.replaceState({}, "", url.toString());
    } catch {}
  }, []);

  return {
    currentUser,
    setCurrentUser,
    authView,
    resetToken,
    handleLogin,
    handleLogout,
    handleUpdateCurrentUser,
    handleShowForgotPassword,
    handleBackToMain,
  };
}
